"use client";

import { useEffect } from "react";
import { toast } from "sonner";

/**
 * Registers the Serwist-generated service worker (`/sw.js`). Mounted once in the
 * root layout, and the ONLY registration point: `register: false` in
 * next.config.ts turns off @serwist/next's injected auto-registration, which
 * fired the moment the main bundle executed — so a first install (or the
 * post-deploy update check) precached in parallel with the page's own chunk
 * downloads and starved them.
 *
 * The SW only exists in production builds (see next.config.ts), so this is a
 * no-op in dev — `navigator.serviceWorker.register` simply 404s and is
 * swallowed. Registration is what makes a fully-offline cold start possible:
 * without an active SW, an offline navigation has nothing to serve.
 *
 * UPDATE HANDLING. The SW uses `skipWaiting`+`clientsClaim`, so a new build's
 * worker activates and takes over fetches immediately — but an already-open tab
 * has ALREADY executed the old JS in memory, and activation doesn't reload it.
 * So the tab keeps running the previous build until it's reloaded; before this,
 * the only way a user picked up a shipped change was to manually clear site
 * data. We now watch for a freshly-installed worker and surface a dismissible
 * "new version — Refresh" toast, and re-check on tab refocus so a long-open tab
 * notices a deploy that landed while the user was elsewhere. We deliberately do
 * NOT auto-reload: this app is mostly form entry, and reloading mid-form would
 * throw away what the user has typed. The user refreshes when it's safe.
 *
 * The Refresh action does NOT just `location.reload()`. iOS standalone PWAs and
 * the Capacitor WKWebView routinely ignore the SW's build-time `skipWaiting` while
 * a client is controlled, so the new build sits stuck in `waiting`, a plain reload
 * re-runs the old worker, and the toast reappears after every refresh — the button
 * looks dead. Instead we message the waiting worker to skip waiting and reload on
 * `controllerchange`, the activation path that actually works cross-browser. (This
 * armed reload only fires from the button, never from clientsClaim's own
 * controllerchange — so an open form is never yanked out from under the user.)
 *
 * AND IT ESCALATES, because on WKWebView that still wasn't enough: the toast came
 * back instantly on the reloaded page, forever. Two things made that unbreakable.
 * The old code reloaded blind after 2s whether or not activation had happened —
 * which on a slow worker start races the activation it just asked for, and
 * otherwise just returns to the identical stuck state with the identical toast.
 * And it messaged a `ServiceWorker` captured when the toast was raised, which by
 * click time may be `redundant` (superseded by a later install); postMessage to a
 * redundant worker is silently a no-op, so the button could never have worked.
 *
 * So: resolve `registration.waiting` LIVE at click time, wait a WebKit-realistic
 * 6s for the worker to actually take over, and if it hasn't, `unregister()` the
 * registration before reloading. An uncontrolled load re-registers from scratch,
 * which is a FIRST install — no controller, so no prompt — and the loop is over in
 * one click instead of never. Cache Storage is origin-scoped and survives
 * unregistration, so the precache is re-validated, not refetched: this costs a
 * slow refresh, not the offline capability.
 *
 * AND THE PROMPT ITSELF MUST BE REFUSABLE. All of the above is about making the
 * Refresh button work; none of it helps a user who does not want to refresh right
 * now. The toast had no close button, an infinite duration, and an action button
 * that only acted — so on a day with two deploys, a prompt raised while a job
 * dialog was open sat over that dialog's Save button with no way to move it, and
 * the only remedy on offer was the update itself, mid-form. So it now carries both
 * a "Later" and an X, and dismissing snoozes further prompts for the rest of the
 * tab session (sessionStorage — a fresh tab should hear about a new build again).
 * Snoozing hides the PROMPT, never the update: `skipWaiting` has already put the
 * new worker in charge, so the next ordinary reload picks the build up regardless.
 */
export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    let registered = false;
    let promptShown = false;
    let reloading = false;
    let swRegistration: ServiceWorkerRegistration | null = null;
    let onVisible: (() => void) | null = null;

    const reloadOnce = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };

    // How long to let the new worker actually take over before giving up on the
    // polite path. The old value was 2s, which is under the cold-start time of a
    // service worker on a sleeping WKWebView — long enough to reload out from under
    // an activation that was still in progress.
    const ACTIVATION_BUDGET_MS = 6000;

    /**
     * "Later" has to OUTLIVE the tab's current page, or it isn't a dismissal.
     *
     * The prompt is raised at most once per page load, so a per-mount flag alone
     * means every reload re-raises it — and on a day with several deploys, so does
     * every tab refocus, because the visibility handler below pulls the newest
     * worker. Dismissing has to be remembered somewhere the next page load reads.
     *
     * sessionStorage, not localStorage: the snooze belongs to this stretch of work.
     * A new tab (or tomorrow) should be told about a new build again — the whole
     * point of the prompt is that this tab is running stale JS.
     */
    const SNOOZE_KEY = "sw-update-snoozed";
    const isSnoozed = () => {
      try {
        return window.sessionStorage.getItem(SNOOZE_KEY) === "1";
      } catch {
        return false; // private mode / storage disabled — prompting is the safe side
      }
    };
    const snooze = () => {
      try {
        window.sessionStorage.setItem(SNOOZE_KEY, "1");
      } catch {
        // Nothing to do; worst case the prompt returns on the next load.
      }
    };

    /**
     * A refresh the user just ACCEPTED must buy a moment's quiet.
     *
     * `promptShown` only silences the prompt within one page load, and the whole
     * point of Refresh is to end that page load — so the reload lands on a fresh
     * mount with the guard reset, and anything that installs a worker on the way
     * back up prompts again about ten seconds later. From the user's side the
     * button plainly did not work; they press it again, and it does not work
     * again. Two open tabs is the same story: each one's registration races the
     * other's, so the tab you refreshed can be re-prompted by the tab you didn't.
     *
     * That re-prompt is never news. The user acted on this exact message seconds
     * ago and the page they are looking at is the result. So an accepted refresh
     * stamps the clock, and a prompt raised inside the window below is dropped
     * rather than shown — the update itself is unaffected (`skipWaiting` has
     * already handed over), and a build that genuinely lands later still gets
     * through, because the window is far shorter than the gap between deploys.
     *
     * A window, not a flag: a flag would swallow the rest of the tab's session,
     * and this must not become a way to never hear about a new build again.
     */
    const ACCEPTED_KEY = "sw-update-accepted-at";
    const ACCEPTED_QUIET_MS = 2 * 60 * 1000;
    const markAccepted = () => {
      try {
        window.sessionStorage.setItem(ACCEPTED_KEY, String(Date.now()));
      } catch {
        // Storage disabled — fall back to the previous (noisier) behaviour.
      }
    };
    const recentlyAccepted = () => {
      try {
        const at = Number(window.sessionStorage.getItem(ACCEPTED_KEY));
        // A clock that moved backwards (or a garbage value) must not read as a
        // quiet period that never ends.
        return Number.isFinite(at) && at > 0 && Date.now() - at < ACCEPTED_QUIET_MS;
      } catch {
        return false;
      }
    };

    // Promote the waiting worker to controller, THEN reload — see the file header.
    const activateAndReload = () => {
      // Stamp BEFORE anything can reload out from under us, and on every path
      // through this function: the early plain-reload below is the common
      // Chromium case, and it is the one that was re-prompting on the way back up.
      markAccepted();

      // Resolve the waiting worker NOW. A worker captured when the toast was raised
      // may since have been superseded and gone `redundant`, and postMessage to a
      // redundant worker fails silently — the click would do nothing at all.
      const registration = swRegistration;
      const waiting = registration?.waiting ?? null;

      // Nothing waiting (it already took over — the common Chromium case), or this
      // document was never controlled: a plain reload is the whole job.
      if (!registration || !waiting || !navigator.serviceWorker.controller) {
        reloadOnce();
        return;
      }

      // Reusing the id REPLACES the prompt in place. Sonner merges the new data
      // over the old toast, so the prompt's buttons and description have to be
      // cleared by hand or they ride along under the spinner — including a
      // `cancel` that would snooze an update the user just accepted.
      toast.loading("Updating…", {
        id: "sw-update",
        duration: Infinity,
        description: undefined,
        closeButton: false,
        onDismiss: undefined,
        cancel: undefined,
        action: undefined,
      });

      // Either signal means the new build is live: `controllerchange` when it claims
      // this client, `activated` when it took over without claiming.
      navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, { once: true });
      waiting.addEventListener("statechange", () => {
        if (waiting.state === "activated") reloadOnce();
      });
      waiting.postMessage({ type: "SKIP_WAITING" });

      // Last resort. `skipWaiting` did not take on this device, and reloading while
      // still controlled by the old worker just re-raises this same toast forever.
      // Dropping the registration guarantees the next load starts clean.
      window.setTimeout(() => {
        if (reloading) return;
        registration
          .unregister()
          .catch(() => {})
          .then(reloadOnce);
      }, ACTIVATION_BUDGET_MS);
    };

    const promptRefresh = () => {
      if (promptShown || isSnoozed() || recentlyAccepted()) return;
      promptShown = true;
      toast("A new version is available", {
        id: "sw-update",
        description: "Refresh to load the latest updates.",
        duration: Infinity,
        /**
         * An infinite-duration toast whose only button ACTS cannot be closed at
         * all: sonner renders no close affordance unless asked, and clicking the
         * body does nothing. So the prompt parked itself in the bottom-right of the
         * viewport forever — which on this app is exactly where a dialog's
         * Cancel/Save buttons sit, so the only escape was to take an update you may
         * be mid-form and not want.
         *
         * Two ways out, because sonner routes them through different callbacks and
         * users reach for both: `cancel` fires only `cancel.onClick`, while the X
         * (and a mobile swipe) fires only `onDismiss`. Each must snooze on its own.
         */
        closeButton: true,
        onDismiss: snooze,
        cancel: {
          label: "Later",
          onClick: snooze,
        },
        action: {
          label: "Refresh",
          onClick: (event) => {
            // Sonner deletes the toast after an action click unless the handler
            // prevents it — which would take the "Updating…" state below down with
            // it, leaving the 6s activation wait looking like a dead button.
            event.preventDefault();
            activateAndReload();
          },
        },
      });
    };

    const watchForUpdate = (registration: ServiceWorkerRegistration) => {
      swRegistration = registration;

      // A worker that reaches `installed` while a controller already exists is an
      // UPDATE (not the first install) — a new build is ready to run.
      const promptIfUpdate = (worker: ServiceWorker | null) => {
        if (!worker) return;
        const check = () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            promptRefresh();
          }
        };
        check(); // it may already be installed by the time we attach
        worker.addEventListener("statechange", check);
      };

      // Already waiting (installed between page load and this effect running),
      // plus any worker that starts installing from here on.
      promptIfUpdate(registration.waiting);
      registration.addEventListener("updatefound", () =>
        promptIfUpdate(registration.installing),
      );

      // A long-open tab won't re-fetch /sw.js on its own until a navigation —
      // nudge an update check when the tab regains focus so a deploy that landed
      // while the user was elsewhere gets noticed promptly.
      onVisible = () => {
        if (document.visibilityState === "visible") registration.update().catch(() => {});
      };
      document.addEventListener("visibilitychange", onVisible);
    };

    /**
     * Ask the ACTIVE worker to download the background tier (the offline map
     * engine — see "Two tiers" in app/sw.ts). It lives outside the precache so
     * it can never hold up activation; this message is what gets it fetched,
     * because a message event's `waitUntil` keeps the worker alive through the
     * download. Idempotent and cheap once warm (a cache lookup per file), so it
     * runs on every load — that is also the retry for any file that failed.
     * `ready` never settles if the install failed; then there's nothing to warm.
     */
    const warmOfflineExtrasWhenActive = () => {
      void navigator.serviceWorker.ready.then((registration) => {
        const kick = () => registration.active?.postMessage({ type: "WARM_OFFLINE_EXTRAS" });
        if (typeof window.requestIdleCallback === "function") {
          window.requestIdleCallback(kick, { timeout: 10_000 });
        } else {
          window.setTimeout(kick, 3000);
        }
      });
    };

    const register = () => {
      if (registered) return;
      registered = true;
      navigator.serviceWorker
        .register("/sw.js", { scope: "/" })
        .then((registration) => {
          if (registration) watchForUpdate(registration);
          warmOfflineExtrasWhenActive();
        })
        .catch(() => {
          // No SW in dev / unsupported browser — offline cold start just won't be
          // available, which is the pre-existing behaviour. Never throw.
        });
    };
    // Defer past `load`, then into idle time: `load` fires when the document's
    // resources finish, but hydration and the first data fetches are often
    // still running — registering (and the install/update it can trigger)
    // should never contend with them. The 3s timeout bounds the wait on pages
    // that never go idle; Safari has no requestIdleCallback, hence the check.
    const registerWhenIdle = () => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(register, { timeout: 3000 });
      } else {
        window.setTimeout(register, 1500);
      }
    };
    // Absolute backstop: one slow image (or a hung request) can hold `load`
    // open indefinitely, and everything that waits for SW control — the
    // offline shell warm, `navigator.serviceWorker.ready` in the jobs
    // prefetch — would wait just as long. Registration must be LATE, never
    // absent.
    const backstop = window.setTimeout(register, 5000);
    const cleanup = () => {
      window.clearTimeout(backstop);
      if (onVisible) document.removeEventListener("visibilitychange", onVisible);
    };
    if (document.readyState === "complete") {
      registerWhenIdle();
      return cleanup;
    }
    window.addEventListener("load", registerWhenIdle, { once: true });
    return () => {
      cleanup();
      window.removeEventListener("load", registerWhenIdle);
    };
  }, []);

  return null;
}
